import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { CustomerShell } from "@/components/CustomerShell";
import { toast } from "sonner";
import { detectLocation, geoErrorText } from "@/lib/geo";
import { House, Loader2, MapPin, Users, Plus, X, ChevronRight, Wrench, Star, Building2, Hotel, Briefcase, BedDouble, Building, Crosshair } from "lucide-react";

export default function MyHome() {
    const [homes, setHomes] = useState(null);
    const [areas, setAreas] = useState([]);
    const [adding, setAdding] = useState(false);
    const { t } = useLang();
    const nav = useNavigate();

    const load = () => api.get("/homes").then((r) => setHomes(r.data)).catch(() => setHomes([]));
    useEffect(() => { load(); api.get("/service-areas?all=true").then((r) => setAreas(r.data)).catch(() => {}); }, []);
    useEffect(() => { if (new URLSearchParams(window.location.search).get("add") === "1") setAdding(true); }, []);

    return (
        <CustomerShell>
            <div className="rounded-b-3xl bg-navy px-5 pb-5 pt-6 text-white">
                <h1 className="font-heading text-2xl font-extrabold">{t("My Homes")}</h1>
                <p className="text-sm text-white/60">{t("Every HOMIIE home and its living service record")}</p>
            </div>
            <div className="px-5 py-5">
                <button data-testid="my-team-link" onClick={() => nav("/my-team")} className="mb-4 flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left transition-transform active:scale-[0.98]">
                    <div className="rounded-xl bg-orange/10 p-2.5"><Users size={20} className="text-orange" /></div>
                    <div className="flex-1">
                        <h3 className="font-heading font-bold text-navy">{t("My HOMIIE Team")}</h3>
                        <p className="text-xs text-navy/50">{t("Your preferred professionals")}</p>
                    </div>
                    <ChevronRight size={18} className="text-navy/30" />
                </button>

                {homes === null && <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}
                {homes && homes.length === 0 && (
                    <div className="mt-6 flex flex-col items-center text-center">
                        <div className="rounded-full bg-navy/5 p-5"><House size={40} className="text-navy/30" /></div>
                        <h3 className="mt-4 font-heading text-lg font-bold text-navy">No home yet</h3>
                        <p className="mt-1 text-sm text-navy/50">Add a home or make your first booking.</p>
                    </div>
                )}

                <div className="space-y-3">
                    {homes?.map((h) => (
                        <button key={h.id} data-testid={`home-card-${h.home_id}`} onClick={() => nav(`/my-home/${h.id}`)} className="flex w-full items-center gap-4 rounded-2xl border border-navy/10 bg-white p-4 text-left shadow-[0_4px_16px_rgba(16,42,67,0.04)] transition-transform active:scale-[0.98]">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-navy"><House size={22} className="text-orange" /></div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <h3 className="truncate font-heading text-base font-extrabold text-navy">{h.label}</h3>
                                    {h.property_type && <span className="shrink-0 rounded-full bg-navy/5 px-2 py-0.5 text-[10px] font-bold text-navy/55">{h.property_type}</span>}
                                    {h.home_score != null && <span className="flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[11px] font-bold text-emerald-700"><Star size={10} className="fill-emerald-500 text-emerald-500" />{h.home_score}</span>}
                                </div>
                                <div className="mt-0.5 flex items-center gap-1 text-xs text-navy/55"><MapPin size={12} className="shrink-0 text-navy/35" /><span className="truncate">{h.address_line}{h.society ? `, ${h.society}` : ""}</span></div>
                                <p className="mt-1 text-xs font-semibold text-orange-dark">{h.home_id} · {h.service_count} service{h.service_count === 1 ? "" : "s"}{h.last_service ? ` · last ${h.last_service}` : ""}</p>
                            </div>
                            <ChevronRight size={18} className="shrink-0 text-navy/30" />
                        </button>
                    ))}
                </div>

                {homes !== null && (
                    <button data-testid="add-home-btn" onClick={() => setAdding(true)} className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-full border-2 border-dashed border-orange/40 py-3 text-sm font-bold text-orange-dark active:scale-95"><Plus size={16} /> {t("ADD NEW HOME")}</button>
                )}
            </div>

            {adding && <AddHome areas={areas.filter((a) => a.enabled !== false) } allAreas={areas} onClose={() => setAdding(false)} onDone={(id) => { setAdding(false); load(); if (id) nav(`/my-home/${id}`); }} />}
        </CustomerShell>
    );
}

const PROPERTY_TYPES = [
    { key: "Home", icon: House },
    { key: "Flat", icon: Building2 },
    { key: "Villa", icon: Hotel },
    { key: "Office", icon: Briefcase },
    { key: "Hotel", icon: BedDouble },
    { key: "Society", icon: Building },
    { key: "Other", icon: MapPin },
];

function AddHome({ allAreas, onClose, onDone }) {
    const [f, setF] = useState({ label: "", address_line: "", society: "", area_id: "", bhk: "", property_type: "Home", lat: null, lng: null, pincode: "", city: "" });
    const [busy, setBusy] = useState(false);
    const [locating, setLocating] = useState(false);
    const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
    const selArea = allAreas.find((a) => a.id === f.area_id);

    const matchPincode = async () => {
        if (!/^\d{6}$/.test(f.pincode)) return;
        try {
            const { data } = await api.post("/location/resolve", { pincode: f.pincode });
            set("area_id", data.area?.id || "");
        } catch { /* ignore */ }
    };

    const detect = async () => {
        setLocating(true);
        try {
            const g = await detectLocation();
            setF((s) => ({ ...s, address_line: g.address_line || s.address_line, society: g.society || s.society, lat: g.lat, lng: g.lng,
                pincode: g.pincode || "", city: g.city || "", area_id: g.area?.id || "" }));
            toast.success(g.services_available ? "Location detected — Services available here" : "Location detected — Care available, Services coming soon here");
        } catch (e) { toast.error(geoErrorText(e)); }
        finally { setLocating(false); }
    };

    const submit = async () => {
        if (!f.label.trim() || !f.address_line.trim()) return toast.error("Fill name & address");
        setBusy(true);
        try { const { data } = await api.post("/homes", f); toast.success("Address saved"); onDone(data.id); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-navy/50 backdrop-blur-sm" onClick={onClose}>
            <div className="mx-auto max-h-[88vh] w-full max-w-md animate-fade-up overflow-y-auto rounded-t-3xl bg-white p-6 pb-8 homiie-scroll" onClick={(e) => e.stopPropagation()}>
                <div className="mb-3 flex items-center justify-between"><h3 className="font-heading text-lg font-extrabold text-navy">Add an address</h3><button data-testid="add-home-close" onClick={onClose}><X size={20} className="text-navy/50" /></button></div>

                <button data-testid="detect-location-btn" onClick={detect} disabled={locating} className="mb-1 flex w-full items-center justify-center gap-2 rounded-xl bg-navy py-3 text-sm font-bold text-white disabled:opacity-60">
                    {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair size={16} className="text-orange" />} {locating ? "Detecting…" : "Use my current location"}
                </button>
                {f.lat != null && <p className="mb-2 text-center text-[11px] font-semibold text-emerald-600">Location pinned ({f.lat.toFixed(4)}, {f.lng.toFixed(4)})</p>}
                <p className="mb-3 text-center text-[11px] text-navy/40">— or enter your address manually below —</p>

                <label className="mb-1.5 block text-sm font-semibold text-navy">Property type</label>
                <div className="mb-3 grid grid-cols-4 gap-2">
                    {PROPERTY_TYPES.map((p) => {
                        const Ic = p.icon; const active = f.property_type === p.key;
                        return <button key={p.key} data-testid={`ptype-${p.key.toLowerCase()}`} onClick={() => { set("property_type", p.key); if (!f.label) set("label", p.key); }} className={`flex flex-col items-center gap-1 rounded-xl border py-2.5 text-[11px] font-bold transition-colors ${active ? "border-orange bg-orange/10 text-orange-dark" : "border-navy/12 text-navy/55"}`}><Ic size={16} />{p.key}</button>;
                    })}
                </div>

                <In testid="home-label" label="Address name" ph="e.g. Rajesh Residence / Papa's Flat" value={f.label} onChange={(v) => set("label", v)} />
                <In testid="home-address" label="Full address" ph="House / flat no., street, landmark" value={f.address_line} onChange={(v) => set("address_line", v)} />
                <In testid="home-society" label="Society / building (optional)" value={f.society} onChange={(v) => set("society", v)} />
                <label className="mb-1.5 mt-2 block text-sm font-semibold text-navy">Area</label>
                <select data-testid="home-area" value={f.area_id} onChange={(e) => set("area_id", e.target.value)} className="mb-2 w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none focus:border-orange">
                    {allAreas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    <option value="">Other area (Care only for now)</option>
                </select>
                <p data-testid="home-area-availability" className={`mb-2 text-[11px] font-semibold ${selArea?.enabled ? "text-emerald-600" : "text-amber-600"}`}>
                    {selArea?.enabled ? "Services & Care available at this address" : "Care available · Services coming soon to this area"}
                </p>
                <In testid="home-pincode" label="Pincode (optional)" ph="e.g. 342008" value={f.pincode} onBlur={matchPincode} onChange={(v) => set("pincode", v.replace(/\D/g, "").slice(0, 6))} />
                <In testid="home-bhk" label="BHK (optional)" ph="e.g. 2BHK" value={f.bhk} onChange={(v) => set("bhk", v)} />
                <button data-testid="save-home-btn" disabled={busy} onClick={submit} className="mt-3 w-full rounded-full bg-orange py-3 text-sm font-bold text-white disabled:opacity-60">{busy ? "Saving…" : "SAVE ADDRESS"}</button>
            </div>
        </div>
    );
}

function In({ label, value, onChange, onBlur, ph, testid }) {
    return <div className="mb-2"><label className="mb-1.5 block text-sm font-semibold text-navy">{label}</label><input data-testid={testid} value={value} onBlur={onBlur} onChange={(e) => onChange(e.target.value)} placeholder={ph} className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none focus:border-orange" /></div>;
}
